import { useCallback, useMemo, useState } from 'react'

import { AutocompleteInputChangeReason } from '@mui/material'

import { useUsers } from '@tabeeb/modules/../users/hooks'
import { Chip, UserInfo } from '@collaborate-health/uikit'
import { FormAutocomplete, FormAutocompleteProps } from '@collaborate-health/forms'
import { UserSearchInfoResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

export interface FormUserSelectProps<TFreeSolo extends boolean = false, TMultiple extends boolean = false>
  extends Omit<FormAutocompleteProps<number, UserSearchInfoResponse, TMultiple, undefined, TFreeSolo>, 'options'> {
  minSearchLength?: number
}

const FormUserSelect = <TFreeSolo extends boolean = false, TMultiple extends boolean = false>({
  name = 'Users',
  minSearchLength = 1,
  freeSolo = false as TFreeSolo,
  multiple = false as TMultiple,
  required = false,
  label = 'Enter name or email',
  placeholder = 'Enter name or email...',
  renderOption,
  renderTags,
  ...rest
}: FormUserSelectProps<TFreeSolo, TMultiple>) => {
  const [searchUserEmail, setSearchUserEmail] = useState('')
  const handleChangeUserEmailInput = useCallback((event, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') {
      setSearchUserEmail('')
    } else if (reason === 'input') {
      setSearchUserEmail(value)
    }
  }, [])

  const { loading, users } = useUsers({ search: searchUserEmail, minSearchLength })

  const options = useMemo<FormAutocompleteProps<number, UserSearchInfoResponse>['options']>(() => {
    return users.map((user) => ({
      value: user.Id,
      name: user.Email || user.Name || '',
      meta: user,
    }))
  }, [users])

  return (
    <FormAutocomplete
      {...rest}
      valueMode='object'
      name={name}
      loading={loading}
      freeSolo={freeSolo}
      multiple={multiple}
      options={options}
      label={label}
      placeholder={placeholder ?? 'Enter an email...'}
      onInputChange={handleChangeUserEmailInput}
      filterOptions={(o) => o}
      renderTags={
        renderTags ??
        ((ops, getItemProps) =>
          ops.map(({ meta: user }, index) => (
            <Chip
              variant='two-tone'
              color='primary'
              size='small'
              maxWidth={260}
              label={user!.Email}
              title={user!.UserName!}
              {...getItemProps({ index })}
            />
          )))
      }
      renderOption={
        renderOption ??
        ((props, option) => (
          <li {...props}>
            <UserInfo item={option.meta!} />
          </li>
        ))
      }
    />
  )
}

export default FormUserSelect

